import { useEffect, useState } from 'react';
import axios from 'axios';
import vopayApi from '@/services/api.config';
import {
  PayPalRecipientsRequest,
  PayPalRecipientsResponse,
  PayPalWithdrawRequest,
  PayPalWithdrawResponse,
  PayPalWithdrawTransactionResponse,
  PayPalDefaultRecipientRequest,
  PayPalDefaultRecipientResponse,
} from 'vopay';

const { api } = vopayApi;

export default function usePayPal({ bearer }: { bearer?: string } = {}) {
  const source = axios.CancelToken.source();

  const [transaction, setTransaction] = useState<PayPalWithdrawTransactionResponse>();
  const [transactionLoading, setTransactionLoading] = useState(false);

  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipients, setRecipients] = useState<PayPalRecipientsResponse>();

  const [defaultRecipientLoading, setDefaultRecipientLoading] = useState(false);
  const [defaultRecipient, setDefaultRecipient] = useState<PayPalDefaultRecipientResponse>();

  useEffect(() => {
    return () => {
      source.cancel();
    };
  }, []);

  async function fetchPayPalWithdrawTransaction(transactionId: string) {
    try {
      setTransactionLoading(true);
      const res = await api.fetchPayPalWithdrawTransaction({ TransactionID: transactionId }, bearer, source);
      if (!res.Success) {
        setTransaction(undefined);
        throw new Error(res.ErrorMessage);
      }
      setTransaction(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setTransactionLoading(false);
    }
  }

  async function payPalWithdraw(params: PayPalWithdrawRequest): Promise<PayPalWithdrawResponse> {
    try {
      setWithdrawLoading(true);
      const res = await api.postPayPalWithdraw(params, source);
      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setWithdrawLoading(false);
    }
  }

  async function fetchPayPalRecipients(params: PayPalRecipientsRequest = {}) {
    try {
      setRecipientsLoading(true);
      const res = await api.fetchPayPalRecipients(params, source);
      if (!res.Success) {
        setRecipients(undefined);
        throw new Error(res.ErrorMessage);
      }
      setRecipients(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setRecipientsLoading(false);
    }
  }

  async function fetchPayPalDefaultRecipient(
    params: PayPalDefaultRecipientRequest = {},
  ): Promise<PayPalDefaultRecipientResponse> {
    try {
      setDefaultRecipientLoading(true);
      const res = await api.fetchPayPalDefaultRecipient(params, source);
      if (!res.Success) {
        setDefaultRecipient(undefined);
        throw new Error(res.ErrorMessage);
      }
      setDefaultRecipient(res);
      return res;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setDefaultRecipientLoading(false);
    }
  }

  return {
    transaction,
    transactionLoading,
    withdrawLoading,
    recipientsLoading,
    recipients,
    defaultRecipientLoading,
    defaultRecipient,
    fetchPayPalWithdrawTransaction,
    payPalWithdraw,
    fetchPayPalRecipients,
    fetchPayPalDefaultRecipient,
  };
}
